'use client';
import { tva } from '@gluestack-ui/nativewind-utils/tva';
import { cssInterop } from 'nativewind';
import React from 'react';
import { ActivityIndicator } from 'react-native';

import { useColors } from '@/hooks/useColors';

cssInterop(ActivityIndicator, {
  className: { target: 'style', nativeStyleToProp: { color: true } },
});

const spinnerStyle = tva({});

/**
 * Thin wrapper around React Native's `ActivityIndicator`.
 *
 * # Brand color by default
 *
 * `ActivityIndicator` ignores Tailwind classes for color — it
 * requires a real value through the `color` prop. To keep the
 * loader visually consistent with the rest of the design system
 * without forcing every caller to thread a hex through, we default
 * `color` to `colors.primary[700]`. Callers that want a different
 * tone (inverse on solid buttons, muted for empty states) pass
 * `color` explicitly and that value wins.
 */
const Spinner = React.forwardRef<
  React.ElementRef<typeof ActivityIndicator>,
  React.ComponentProps<typeof ActivityIndicator>
>(({ className, color, focusable = false, 'aria-label': ariaLabel = 'loading', ...props }, ref) => {
  const { colors } = useColors();

  const resolvedColor = color ?? colors.primary[700];

  return (
    <ActivityIndicator
      ref={ref}
      focusable={focusable}
      aria-label={ariaLabel}
      {...props}
      color={resolvedColor}
      className={spinnerStyle({ class: className })}
    />
  );
});

Spinner.displayName = 'Spinner';

export { Spinner };
